import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Search, Plus, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Client } from '../types';
import {
  FLAT_RATE_BASE, REVENUE_SHARE_BASE, RESTROOM_FEE,
  INSURANCE_WAIVER_FEE, fmtMoney, addDays,
} from '../lib/pricing';

const EVENT_TYPES = ['Birthday Party', 'Concert', 'DJ Night', 'Private Party', 'Other'];

interface FormData {
  // Step 1
  clientId: string;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  clientInstagram: string;
  isNewClient: boolean;
  // Step 2
  eventDate: string;
  startTime: string;
  eventType: string;
  estimatedAttendance: string;
  pricingModel: 'flat_rate' | 'revenue_share';
  // Step 3
  restroomProvided: boolean;
  insuranceOption: 'policy' | 'waiver_fee';
  notes: string;
}

const initial: FormData = {
  clientId: '', clientName: '', clientPhone: '', clientEmail: '', clientInstagram: '', isNewClient: false,
  eventDate: '', startTime: '18:00', eventType: 'Birthday Party', estimatedAttendance: '',
  pricingModel: 'flat_rate', restroomProvided: true, insuranceOption: 'policy', notes: '',
};

export function NewBooking() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(initial);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    supabase.from('clients').select('*').order('name').then(({ data }) => setClients(data ?? []));
  }, []);

  const f = (key: keyof FormData, val: any) => setForm(prev => ({ ...prev, [key]: val }));

  const restroomFee = form.restroomProvided ? 0 : RESTROOM_FEE;
  const insuranceFee = form.insuranceOption === 'waiver_fee' ? INSURANCE_WAIVER_FEE : 0;
  const baseFee = form.pricingModel === 'flat_rate' ? FLAT_RATE_BASE : REVENUE_SHARE_BASE;
  const totalOwed = baseFee + restroomFee + insuranceFee;

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (c.phone ?? '').includes(clientSearch)
  );

  const validateStep1 = () => {
    const errs: Record<string, string> = {};
    if (!form.isNewClient && !form.clientId) errs.client = 'Select an existing client or create a new one';
    if (form.isNewClient && !form.clientName.trim()) errs.clientName = 'Name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs: Record<string, string> = {};
    if (!form.eventDate) errs.eventDate = 'Event date is required';
    if (!form.startTime) errs.startTime = 'Start time is required';
    const [h] = form.startTime.split(':').map(Number);
    if (h < 18) errs.startTime = 'Start time must be 6:00 PM or later';
    const att = parseInt(form.estimatedAttendance);
    if (form.estimatedAttendance && att > 450) errs.attendance = 'Maximum attendance is 450 people';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      let clientId = form.clientId;

      if (form.isNewClient) {
        const { data: newClient, error } = await supabase.from('clients').insert({
          name: form.clientName.trim(),
          phone: form.clientPhone || null,
          email: form.clientEmail || null,
          instagram: form.clientInstagram || null,
        }).select().single();
        if (error) throw error;
        clientId = newClient.id;
      }

      const eventDate = new Date(form.eventDate + 'T12:00:00');
      const paymentDueDate = addDays(eventDate, 30);
      const dueDateStr = paymentDueDate.toISOString().split('T')[0];

      const { data: newEvent, error: eventError } = await supabase.from('events').insert({
        client_id: clientId,
        event_date: form.eventDate,
        start_time: form.startTime,
        event_type: form.eventType,
        estimated_attendance: form.estimatedAttendance ? parseInt(form.estimatedAttendance) : null,
        pricing_model: form.pricingModel,
        restroom_provided: form.restroomProvided,
        restroom_fee: restroomFee,
        insurance_option: form.insuranceOption,
        insurance_fee: insuranceFee,
        payment_due_date: dueDateStr,
        notes: form.notes || null,
        status: 'upcoming',
      }).select().single();
      if (eventError) throw eventError;

      await Promise.all([
        supabase.from('contracts').insert({ event_id: newEvent.id, client_id: clientId }),
        supabase.from('cleaning_checklist').insert({ event_id: newEvent.id }),
      ]);

      navigate(`/events/${newEvent.id}`);
    } catch (err: any) {
      alert('Error creating booking: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const steps = ['Client', 'Event Details', 'Fees', 'Review'];

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <button onClick={() => step === 1 ? navigate(-1) : setStep(s => s - 1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6">
        <ArrowLeft size={16} /> {step === 1 ? 'Cancel' : 'Back'}
      </button>

      <h1 className="font-serif text-2xl font-bold text-gray-900 mb-2">New Booking</h1>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1.5 ${i + 1 === step ? 'text-gold-600' : i + 1 < step ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${i + 1 === step ? 'border-gold-500 bg-gold-50' : i + 1 < step ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                {i + 1 < step ? <Check size={12} /> : i + 1}
              </div>
              <span className="text-xs font-medium hidden sm:block">{s}</span>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${i + 1 < step ? 'bg-green-400' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1: Client */}
      {step === 1 && (
        <div className="space-y-4">
          <h2 className="font-semibold text-gray-900">Select or create a client</h2>

          {!form.isNewClient ? (
            <>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  placeholder="Search existing clients…"
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                />
              </div>

              {clientSearch && (
                <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden max-h-48 overflow-y-auto">
                  {filteredClients.map(c => (
                    <button
                      key={c.id}
                      onClick={() => { f('clientId', c.id); f('clientName', c.name); setClientSearch(c.name); }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${form.clientId === c.id ? 'bg-gold-50' : ''}`}
                    >
                      <p className="text-sm font-medium text-gray-900">{c.name}</p>
                      <p className="text-xs text-gray-500">{c.phone || c.email || 'No contact'}</p>
                    </button>
                  ))}
                  {filteredClients.length === 0 && (
                    <div className="px-4 py-3 text-sm text-gray-400">No clients found</div>
                  )}
                </div>
              )}

              {form.clientId && (
                <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                  <Check size={14} /> Selected: {form.clientName}
                </div>
              )}

              <button
                onClick={() => { f('isNewClient', true); f('clientId', ''); setClientSearch(''); }}
                className="flex items-center gap-2 text-gold-600 text-sm font-medium"
              >
                <Plus size={16} /> Create new client
              </button>
            </>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  value={form.clientName}
                  onChange={e => f('clientName', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                  placeholder="Client's full name"
                />
                {errors.clientName && <p className="text-xs text-red-500 mt-1">{errors.clientName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="tel" value={form.clientPhone} onChange={e => f('clientPhone', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                  placeholder="(555) 123-4567" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={form.clientEmail} onChange={e => f('clientEmail', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                  placeholder="client@email.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                <input value={form.clientInstagram} onChange={e => f('clientInstagram', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                  placeholder="@handle" />
              </div>
              <button onClick={() => { f('isNewClient', false); f('clientName', ''); }} className="text-sm text-gray-500 underline">
                Use existing client instead
              </button>
            </div>
          )}

          {errors.client && <p className="text-sm text-red-500">{errors.client}</p>}
        </div>
      )}

      {/* STEP 2: Event Details */}
      {step === 2 && (
        <div className="space-y-4">
          <h2 className="font-semibold text-gray-900">Event Details</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Date *</label>
            <input
              type="date"
              value={form.eventDate}
              onChange={e => f('eventDate', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
            />
            {errors.eventDate && <p className="text-xs text-red-500 mt-1">{errors.eventDate}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Time * (6:00 PM or later)</label>
            <input
              type="time"
              value={form.startTime}
              onChange={e => f('startTime', e.target.value)}
              min="18:00"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
            />
            {errors.startTime && <p className="text-xs text-red-500 mt-1">{errors.startTime}</p>}
            <p className="text-xs text-gray-400 mt-1">End time is always 12:30 AM</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <select
              value={form.eventType}
              onChange={e => f('eventType', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500 bg-white"
            >
              {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Attendance (max 450)</label>
            <input
              type="number"
              value={form.estimatedAttendance}
              onChange={e => f('estimatedAttendance', e.target.value)}
              min="1"
              max="450"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              placeholder="e.g. 150"
            />
            {errors.attendance && <p className="text-xs text-red-500 mt-1">{errors.attendance}</p>}
            {form.estimatedAttendance && parseInt(form.estimatedAttendance) > 400 && parseInt(form.estimatedAttendance) <= 450 && (
              <p className="text-xs text-yellow-600 mt-1">⚠️ Getting close to the 450-person limit</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pricing Model</label>
            <div className="grid grid-cols-2 gap-3">
              {([['flat_rate', 'Flat Rate', '$1,500'], ['revenue_share', 'Revenue Share', '$1,000 + 50%']] as const).map(([val, label, sub]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => f('pricingModel', val)}
                  className={`p-3 rounded-xl border-2 text-left transition-colors ${
                    form.pricingModel === val ? 'border-gold-500 bg-gold-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="text-sm font-semibold text-gray-900">{label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: Additional Fees */}
      {step === 3 && (
        <div className="space-y-5">
          <h2 className="font-semibold text-gray-900">Additional Options</h2>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm font-medium text-gray-900 mb-3">Portable Restroom</p>
            <div className="flex gap-3">
              {([true, false] as const).map(val => (
                <button
                  key={String(val)}
                  type="button"
                  onClick={() => f('restroomProvided', val)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                    form.restroomProvided === val ? 'border-gold-500 bg-gold-50 text-gold-700' : 'border-gray-200 text-gray-600'
                  }`}
                >
                  {val ? 'Client provides (+$0)' : 'No restroom (+$300)'}
                </button>
              ))}
            </div>
            {!form.restroomProvided && (
              <p className="text-xs text-orange-600 mt-2">$300 restroom fee will be added</p>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm font-medium text-gray-900 mb-3">Event Insurance</p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => f('insuranceOption', 'policy')}
                className={`w-full py-2 px-3 rounded-lg text-sm font-medium border-2 text-left transition-colors ${
                  form.insuranceOption === 'policy' ? 'border-gold-500 bg-gold-50 text-gold-700' : 'border-gray-200 text-gray-600'
                }`}
              >
                Client gets own policy (+$0)
              </button>
              <button
                type="button"
                onClick={() => f('insuranceOption', 'waiver_fee')}
                className={`w-full py-2 px-3 rounded-lg text-sm font-medium border-2 text-left transition-colors ${
                  form.insuranceOption === 'waiver_fee' ? 'border-gold-500 bg-gold-50 text-gold-700' : 'border-gray-200 text-gray-600'
                }`}
              >
                Pay waiver via Zelle (+$164)
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => f('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500 resize-none"
              placeholder="Any special notes about this event…"
            />
          </div>
        </div>
      )}

      {/* STEP 4: Review */}
      {step === 4 && (
        <div className="space-y-4">
          <h2 className="font-semibold text-gray-900">Review & Confirm</h2>

          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
            {[
              ['Client', form.isNewClient ? form.clientName : clients.find(c => c.id === form.clientId)?.name ?? ''],
              ['Event Date', form.eventDate],
              ['Start Time', form.startTime],
              ['End Time', '12:30 AM (always)'],
              ['Event Type', form.eventType],
              ['Attendance', form.estimatedAttendance ? `~${form.estimatedAttendance} people` : 'Not specified'],
              ['Pricing', form.pricingModel === 'flat_rate' ? 'Flat Rate' : 'Revenue Share'],
              ['Restroom', form.restroomProvided ? 'Client provides' : 'None (+$300)'],
              ['Insurance', form.insuranceOption === 'policy' ? 'Client\'s policy' : 'Waiver (+$164)'],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between px-4 py-2.5 text-sm">
                <span className="text-gray-500">{label}</span>
                <span className="font-medium text-gray-900 text-right max-w-xs">{value}</span>
              </div>
            ))}
          </div>

          {/* Fee breakdown */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-700">Fee Breakdown</p>
            </div>
            <div className="divide-y divide-gray-100">
              {[
                [form.pricingModel === 'flat_rate' ? 'Flat Rate' : 'Revenue Share Base', baseFee],
                ...(restroomFee > 0 ? [['Restroom Fee', restroomFee]] : []),
                ...(insuranceFee > 0 ? [['Insurance Waiver', insuranceFee]] : []),
              ].map(([label, amount]) => (
                <div key={label as string} className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-600">{label}</span>
                  <span className="font-medium">{fmtMoney(amount as number)}</span>
                </div>
              ))}
              <div className="flex justify-between px-4 py-3 font-bold text-gray-900 bg-gold-50">
                <span>Total Owed</span>
                <span className="text-gold-700">{fmtMoney(totalOwed)}</span>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
            ⚠️ Reminder: No alcohol allowed at this venue.
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="mt-8 flex justify-between items-center">
        {step < 4 ? (
          <button
            onClick={handleNext}
            className="ml-auto flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors"
          >
            Next <ArrowRight size={16} />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="ml-auto flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
          >
            {saving ? 'Creating…' : 'Confirm Booking'} {!saving && <Check size={16} />}
          </button>
        )}
      </div>
    </div>
  );
}
